import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpHandlerFn
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

export const authInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  const token = localStorage.getItem(AuthService.BELLABEAT_ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(AuthService.BELLABEAT_REFRESH_TOKEN);

  let isRefreshing = false;
  const refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  if (token && !request.url.startsWith('https://articles.bellabeat.io') && !request.url.startsWith('https://article2.bellabeat.io')) {
    let headers: { [key: string]: string } = { 'x-refresh-token': refreshToken || '' };
    if (!request.url.includes('/auth-ms/token/logout') && !request.url.includes('/auth-ms/token/refresh')) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (!request.headers.has('Content-Type') && !request.url.includes('api.bellabeat.io')) {
      headers['Content-Type'] = 'application/json';
    }
    request = request.clone({ setHeaders: headers });
  }

  return next(request).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401 && !request.url.includes('/auth-ms/token/refresh')) {
        return handle401Error(request, next, authService, refreshTokenSubject, isRefreshing, error);
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  refreshTokenSubject: BehaviorSubject<any>,
  isRefreshing: boolean,
  error: HttpErrorResponse
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = localStorage.getItem(AuthService.BELLABEAT_REFRESH_TOKEN);

    // Nothing to refresh with (e.g. an invalid access code on a fresh session):
    // fail the request instead of waiting for a token that never arrives
    if (!refreshToken) {
      return throwError(() => error);
    }

    if (refreshToken) {
      return authService.refreshToken(refreshToken).pipe(
        switchMap((res: any) => {
          isRefreshing = false;
          const newToken = res.headers.get(AuthService.ACCESS_TOKEN);
          refreshTokenSubject.next(newToken);
          return next(addTokenHeader(request, newToken));
        }),
        catchError((err) => {
          isRefreshing = false;
          authService.signOut();
          return throwError(() => err);
        })
      );
    }
  }

  return refreshTokenSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap((token) => next(addTokenHeader(request, token)))
  );
}

function addTokenHeader(request: HttpRequest<any>, token: string) {
  return request.clone({
    setHeaders: {
      'Authorization': `Bearer ${token}`
    }
  });
}
